import React, { useMemo } from "react";

import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import {
  networkByNameLookup,
  NetworkTypeData,
} from "@hl/shared-features/lib/utils/blockExplorer";
import { Box, Button, Grid, Text, useMantineTheme } from "@mantine/core";

import { NetworkType } from "apollo/graphql.generated";
import BlockchainIcon from "components/BlockchainIcon";

export type Props = {
  contract?: string;
  network?: NetworkType;
  availableNetworks: NetworkType[];
  onNetworkSelection: (network: NetworkType) => void;
  loading: boolean;
};

const NetworkSelectionSection = ({
  contract,
  network,
  availableNetworks,
  onNetworkSelection,
  loading,
}: Props) => {
  const networksDetails = useMemo(() => {
    return availableNetworks
      .map(networkByNameLookup)
      .filter((c) => c != null) as NetworkTypeData[];
  }, [availableNetworks]);

  if (!contract) {
    return <></>;
  }
  if (contract && network && !loading) {
    return <></>;
  }

  return (
    <>
      <Text pt="md" pb="sm" size="sm" weight={500}>
        What blockchain is this contract on?
      </Text>
      <Box py={0}>
        <Grid gutter={"xs"}>
          {networksDetails.map((n, i) => {
            return (
              <Grid.Col span={6} key={i}>
                <BlockchainTile
                  key={i}
                  network={n.type}
                  isLoading={loading && network === n.type}
                  onClick={() => onNetworkSelection(n.type)}
                />
              </Grid.Col>
            );
          })}
        </Grid>
      </Box>
    </>
  );
};

export default NetworkSelectionSection;

const BlockchainTile = ({
  network,
  isLoading,
  onClick,
}: {
  network: NetworkType;
  isLoading?: boolean;
  onClick: () => void;
}) => {
  const theme = useMantineTheme();
  const networkDetails = useMemo(() => networkByNameLookup(network), [network]);
  const icon = useMemo(() => {
    return (
      <BlockchainIcon
        network={networkDetails.type}
        css={{ width: 16, height: 16 }}
      />
    );
  }, [networkDetails]);
  const title = useMemo(() => {
    return networkDetails.displayName;
  }, [networkDetails]);

  return (
    <Button
      h={48}
      fullWidth
      color={SECONDARY_COLOR}
      loading={isLoading}
      loaderProps={{
        color: theme.fn.themeColor(TEXT_COLOR.PRIMARY),
      }}
      leftIcon={icon}
      onClick={onClick}
      styles={{
        leftIcon: {
          width: "auto",
          height: 20,
        },
      }}
      sx={{
        "&[data-loading]:before": { opacity: 0 },
      }}
    >
      {isLoading ? "Finding contract..." : title}
    </Button>
  );
};
